import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { _3_DAYS } from "../../../constants/durations";
import type { CommunicationDatabase } from "../../../types";
import { getNextPageIndex, pageRange } from "../../../utils/pagination";
import { keys } from "./keys";

// "Autre action" rows are synced into `communications` (type 'other') for lack of their own table,
// but they are not exchanges: they get their own list, kept out of the communications one.
export const OTHER_ACTION_TYPE = 'other';

export type CommunicationKind = 'communications' | 'others';

export type CommunicationFilters = {
  subjectId?: string;
  providerId?: string;
  kind?: CommunicationKind;
};

const PAGE_SIZE = 15;

async function getCommunicationsPage(filters: CommunicationFilters, pageIndex: number): Promise<CommunicationDatabase[]> {
  const { from, to } = pageRange(pageIndex, PAGE_SIZE);

  // Secondary order on id keeps pages stable when several items share a date.
  let query = apiClient
    .from('communications')
    .select('*')
    .order('date', { ascending: false })
    .order('id', { ascending: false })
    .range(from, to);

  query = filters.kind === 'others'
    ? query.eq('type', OTHER_ACTION_TYPE)
    : query.neq('type', OTHER_ACTION_TYPE);

  if (filters.subjectId) {
    query = query.eq('subject_id', filters.subjectId);
  }

  if (filters.providerId) {
    query = query.eq('provider_id', filters.providerId);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data ?? [];
}

export function useGetCommunications(filters: CommunicationFilters) {
  const isEnabled = Boolean(filters.subjectId || filters.providerId);

  return useInfiniteQuery({
    queryKey: keys.communicationsPages(filters),
    queryFn: ({ pageParam }) => getCommunicationsPage(filters, pageParam),
    initialPageParam: 0,
    getNextPageParam: getNextPageIndex(PAGE_SIZE),
    enabled: isEnabled,
    staleTime: _3_DAYS,
  });
}
